"use client";

import { cn } from "../../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../avatar";
import type {
	AvatarRenderer,
	BaseInteraction,
	FacepileSize,
	OverflowRenderer,
} from "./types";

// Helper to get display avatar URL with source-aware priority logic
export const getDisplayAvatarUrl = (item: BaseInteraction): string | null => {
	const source = item.avatar_source;
	if (source === "none") return null;
	if (source === "google" && item.google_avatar_url) return item.google_avatar_url;
	if (source === "manual" && item.manual_avatar_url) return item.manual_avatar_url;
	return item.manual_avatar_url || item.google_avatar_url || null;
};

// Helper to get display name
export const getDisplayName = (item: BaseInteraction): string => {
	return item.username ? `@${item.username}` : item.display_name;
};

// Size mappings
const sizeClasses = {
	sm: {
		avatar: "w-5 h-5",
		icon: "text-[10px]",
		button: "h-5 w-5 text-[10px]",
	},
	md: {
		avatar: "w-8 h-8",
		icon: "text-xs",
		button: "h-8 w-8 text-xs",
	},
	lg: {
		avatar: "w-10 h-10",
		icon: "text-sm",
		button: "h-10 w-10 text-xs",
	},
};

/**
 * Default avatar renderer
 * Simple implementation that matches our working test structure
 */
export const defaultAvatarRenderer: AvatarRenderer<BaseInteraction> = (
	item,
	size,
) => {
	const displayAvatar = getDisplayAvatarUrl(item);
	const { avatar: avatarSize, icon: iconSize } = sizeClasses[size];

	return (
		<Avatar
			className={cn(
				avatarSize,
				"relative inline-block border border-background transition-transform hover:z-20 hover:scale-110",
			)}
		>
			<AvatarImage src={displayAvatar || undefined} alt={item.display_name} />
			<AvatarFallback className="bg-primary">
				<span className={cn("font-semibold text-primary-foreground", iconSize)}>
					{(item.username || item.display_name).charAt(0).toUpperCase()}
				</span>
			</AvatarFallback>
		</Avatar>
	);
};

/**
 * Default overflow renderer
 * Simple structure that matches our working test
 */
export const defaultOverflowRenderer: OverflowRenderer = (count, size) => {
	const { avatar: avatarSize, icon: iconSize } = sizeClasses[size];

	return (
		<div
			className={cn(
				avatarSize,
				"rounded-full bg-muted text-foreground border border-background",
				"relative inline-block transition-transform hover:z-20 hover:scale-110",
				"flex items-center justify-center font-medium cursor-pointer",
				iconSize,
			)}
			title={`${count} more user${count > 1 ? "s" : ""}`}
		>
			+{count}
		</div>
	);
};
